import React, { useEffect, useState } from "react";
import { useLogin } from "../hooks/useLogin";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  PackageSearch,
  BookImage,
  LogInIcon,
  UserLock,
  UsersRound,
  ChevronDown,
  ChevronUp,
  ListOrdered,
  UserRoundCog,
  LogOut,
  Highlighter,
  Layers,
  MessageSquare,
  MessageSquareLockIcon,
  Star,
  ShoppingBag,
  Globe,
  FileText,
  Percent,
} from "lucide-react";

interface LoginWidgetProps {
  isAuthenticated: boolean;
  userSettings: ProfileAndSettings | null;
}
export const LoginWidget: React.FC<LoginWidgetProps> = ({
  isAuthenticated,
  userSettings,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loginRequest, setLoginRequest] = useState<LoginRequest>({
    username: "",
    password: "",
  });
  const [menuState, setMenuState] = useState<boolean>(false);
  const { login } = useLogin(setIsLoading, setError);
  const [showLoginWidget, setShowLoginWidget] = useState<boolean>(false);

  useEffect(() => {
    setShowLoginWidget(!isAuthenticated);
  }, [isAuthenticated]);

  return (
    <>
      <div id="login-button">
        {isAuthenticated ? (
          <div className="dropdown is-active is-right">
            <div className="dropdown-trigger">
              <button
                className="button flex items-center is-primary has-text-black"
                aria-haspopup="true"
                aria-controls="dropdown-menu"
                onClick={() => setMenuState(!menuState)}
              >
                <span>Menu</span>
                <span className="icon is-small">
                  {menuState ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </span>
              </button>
            </div>
            <div
              className={`dropdown-menu ${!menuState ? "is-hidden" : ""}`}
              id="dropdown-menu"
              role="menu"
              onClick={() => setMenuState(!menuState)}
            >
              <div className="dropdown-content">
                <Link to={"/"} className="dropdown-item flex items-center">
                  <span className="icon is-small mr-2">
                    <PackageSearch className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <span>Catalog</span>
                </Link>
                <hr className="dropdown-divider" />
                {userSettings?.isOperatorUser() && (
                  <Link
                    to={"/offers"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <ListOrdered className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Propostas</span>
                  </Link>
                )}

                {(userSettings?.isAdmin() ||
                  userSettings?.isMainAccount() ||
                  userSettings?.isReseller() ||
                  userSettings?.isCommercial()) && (
                  <Link
                    to={"/campaigns"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <Percent className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Campanhas</span>
                  </Link>
                )}

                {(userSettings?.isAdmin() || userSettings?.isMainAccount()) && (
                  <Link
                    to={"/minisite"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <BookImage className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Microsite</span>
                  </Link>
                )}

                {userSettings?.isOperatorUser() && (
                  <Link
                    to={"/settings"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <UserRoundCog className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Configurações</span>
                  </Link>
                )}
                {userSettings?.isOperatorUser() && (
                  <hr className="dropdown-divider" />
                )}
                {(userSettings?.isCommercial() || userSettings?.isAdmin()) && (
                  <Link
                    to={"/orders"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <ListOrdered className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Orders</span>
                  </Link>
                )}
                {(userSettings?.isMainAccount() ||
                  userSettings?.isAdmin() ||
                  userSettings?.isCommercial()) && (
                  <Link
                    to={"/users"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <UsersRound className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Users</span>
                  </Link>
                )}
                {(userSettings?.isCommercial() || userSettings?.isAdmin()) &&
                  (userSettings?.chat_settings?.url ?? false) &&
                  (userSettings?.chat_settings?.token ?? false) &&
                  (userSettings?.chat_settings?.tenant_subdomain ?? false) && (
                    <Link
                      to={
                        userSettings?.chat_settings?.url +
                        "/agent-dashboard?tenant_subdomain=" +
                        userSettings?.chat_settings?.tenant_subdomain +
                        "&token=" +
                        userSettings?.chat_settings?.token
                      }
                      target="_blank"
                      className="dropdown-item flex items-center"
                    >
                      <span className="icon is-small mr-2 y-2">
                        <MessageSquare className="w-5 h-5" aria-hidden="true" />
                      </span>
                      <span>Chat</span>
                    </Link>
                  )}
                {userSettings?.isAdmin() && (
                  <Link
                    to={"/catalog"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <BookImage className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Catalog</span>
                  </Link>
                )}
                {userSettings?.isAdmin() && (
                  <Link
                    to={"/highlights"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <Highlighter className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Notícias / Destaques</span>
                  </Link>
                )}
                {userSettings?.isAdmin() && (
                  <Link
                    to={"/categories"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <Layers className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Categorias</span>
                  </Link>
                )}
                {(userSettings?.isAdmin() || userSettings?.isCommercial()) && (
                  <Link
                    to={"/admin/reviews"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <Star className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Avaliações</span>
                  </Link>
                )}
                {(userSettings?.isAdmin() || userSettings?.isCommercial()) && (
                  <Link
                    to={"/oms-orders"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <ShoppingBag className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Gestao de Encomendas</span>
                  </Link>
                )}
                {(userSettings?.isAdmin() || userSettings?.isCommercial()) && (
                  <Link
                    to={"/oms-proposals"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <ShoppingBag className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Propostas de Orçamento</span>
                  </Link>
                )}
                {userSettings?.isAdmin() && (
                  <Link
                    to={"/public-site"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <Globe className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Gestao Site Publico</span>
                  </Link>
                )}
                {userSettings?.isAdmin() && (
                  <Link
                    to={"/cms-admin"}
                    className="dropdown-item flex items-center"
                  >
                    <span className="icon is-small mr-2 y-2">
                      <FileText className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span>Gestao de Conteudos</span>
                  </Link>
                )}
                {userSettings?.isAdmin() &&
                  (userSettings?.chat_settings?.url ?? false) &&
                  (userSettings?.chat_settings?.token ?? false) &&
                  (userSettings?.chat_settings?.tenant_subdomain ?? false) && (
                    <Link
                      to={
                        userSettings?.chat_settings?.url +
                        "/admin-backoffice?tenant_subdomain=" +
                        userSettings?.chat_settings?.tenant_subdomain +
                        "&token=" +
                        userSettings?.chat_settings?.token
                      }
                      target="_blank"
                      className="dropdown-item flex items-center"
                    >
                      <span className="icon is-small mr-2 y-2">
                        <MessageSquareLockIcon
                          className="w-5 h-5"
                          aria-hidden="true"
                        />
                      </span>
                      <span>Chat Backoffice</span>
                    </Link>
                  )}
                <hr className="dropdown-divider" />
                <Link
                  to={"/logout"}
                  className="dropdown-item flex items-center"
                >
                  <span className="icon is-small mr-2 y-2">
                    <LogOut className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <span>Logout</span>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={(e) => {
              e.preventDefault();
              setShowLoginWidget(!showLoginWidget);
            }}
          >
            <UserLock className="w-4 h-4 inline-block mr-2" />
          </button>
        )}
      </div>
      <div
        id="login-widget"
        className={
          `w-100 absolute right-20 top-20 bg-white rounded-lg p-3 shadow-md z-50 ` +
          (showLoginWidget ? "flex" : "hidden")
        }
      >
        <form className="bg-white p-3 w-full">
          <div className="control mb-4">
            <label>E-mail</label>
            <input
              type="text"
              placeholder="Username"
              className="input is-small mr-2"
              onChange={(e) => {
                setLoginRequest({
                  ...loginRequest,
                  username: e.target.value,
                });
              }}
              value={loginRequest.username}
            />
          </div>
          <div className="control mb-6">
            <label>Password</label>
            <input
              type="password"
              placeholder="Password"
              className="input is-small mr-2"
              onChange={(e) => {
                setLoginRequest({
                  ...loginRequest,
                  password: e.target.value,
                });
              }}
              value={loginRequest.password}
            />
          </div>
          <div className="flex is-justify-content-center">
            <button
              type="submit"
              className="button is-small is-primary p-4 rounded-lg shadow-md"
              onClick={(e) => {
                e.preventDefault();
                login(loginRequest)
                  .then((result) => {
                    if (result) {
                      setShowLoginWidget(false);
                    } else {
                      setError("Utilizador ou password inválido.");
                    }
                  })
                  .catch((err) => {
                    console.warn("Login error:", err);
                  });
              }}
            >
              <LogInIcon className="w-6 h-6" />
              <span className="ml-2">Login</span>
            </button>
          </div>
          <div className="text-red-500 mt-2">
            {isLoading ? "Loading..." : error ? error : ""}
          </div>
        </form>
      </div>
    </>
  );
};
